const statisticsApiUri = process.env.aerialPlotConnections.aerialStatisticsApi.baseUrl;

/*
 * Every endpoint here queues an export and returns an ExcelExportJob; the file is produced
 * server-side and collected via GetExcelExportStatus. These functions return the full response
 * envelope (not just `data`) because the HTTP transport status is always 200 — callers must branch
 * on the envelope's `statusCode` field, and on 401/404 `data` is a plain string message.
 * See utilities/excelExportJob.ts for the poll loop that consumes these.
 *
 * `fileName` is the download name, still composed client-side. Send it WITHOUT an extension: the
 * backend appends the one matching what it actually generated, which is what keeps a tabbed export
 * from being labelled .csv (those endpoints ignore isForCSV and always emit a workbook). Omitting it
 * yields a deliberately ugly `export-<jobId>` placeholder, so it is not optional in practice.
 */

function buildUrl(route, params) {
	const query = new URLSearchParams();

	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== null) {
			query.append(key, String(value));
		}
	});

	return `${statisticsApiUri}api/v1/ExcelGenerator/${route}?${query.toString()}`;
}

function requestJob(route, params, method, accessToken, body) {
	const headers = { Authorization: `Bearer ${accessToken}` };

	if (body !== undefined) {
		headers["Content-Type"] = "application/json";
	}

	return fetch(buildUrl(route, params), {
		method,
		headers,
		body: body === undefined ? undefined : JSON.stringify(body),
	}).then((res) => res.json());
}

export function generateFieldHeatmapExcel(
	clientId,
	clientName,
	fieldId,
	currentDateTimeString,
	removeExcluded,
	fileName,
	accessToken,
) {
	return requestJob(
		"GenerateFieldHeatmapExcel",
		{ clientId, clientName, fieldId, currentDateTimeString, removeExcluded, fileName },
		"POST",
		accessToken,
	);
}

export function generateTrialHeatmapExcel(
	clientId,
	clientName,
	trialId,
	isForAdmin,
	currentDateTimeString,
	removeExcluded,
	fileName,
	accessToken,
) {
	return requestJob(
		"GenerateTrialHeatmapExcel",
		{ clientId, clientName, trialId, isForAdmin, currentDateTimeString, removeExcluded, fileName },
		"POST",
		accessToken,
	);
}

export function generateProtocolHeatmapExcel(
	trials,
	clientId,
	clientName,
	isForAdmin,
	currentDateTimeString,
	removeExcluded,
	fileName,
	accessToken,
) {
	return requestJob(
		"GenerateProtocolHeatmapExcel",
		{ clientId, clientName, isForAdmin, currentDateTimeString, removeExcluded, fileName },
		"POST",
		accessToken,
		trials,
	);
}

export function generateStackedTrialHeatmapExcel(
	clientId,
	clientName,
	trialId,
	isForAdmin,
	currentDateTimeString,
	removeExcluded,
	quantifiedRegion,
	includeSubplots,
	isForCSV,
	fileName,
	accessToken,
) {
	return requestJob(
		"GenerateStackedTrialHeatmapExcel",
		{
			clientId,
			clientName,
			trialId,
			isForAdmin,
			currentDateTimeString,
			removeExcluded,
			quantifiedRegion,
			includeSubplots,
			isForCSV,
			fileName,
		},
		"GET",
		accessToken,
	);
}

export function generateTabbedTrialHeatmapExcel(
	clientId,
	clientName,
	trialId,
	isForAdmin,
	currentDateTimeString,
	removeExcluded,
	quantifiedRegion,
	includeSubplots,
	isForCSV,
	fileName,
	accessToken,
) {
	return requestJob(
		"GenerateTabbedTrialHeatmapExcel",
		{
			clientId,
			clientName,
			trialId,
			isForAdmin,
			currentDateTimeString,
			removeExcluded,
			quantifiedRegion,
			includeSubplots,
			isForCSV,
			fileName,
		},
		"GET",
		accessToken,
	);
}

export function generatePlotGPSTrialCoordsExcel(
	clientId,
	clientName,
	trialId,
	currentDateTimeString,
	quantifiedRegion,
	includeSubplots,
	fileName,
	accessToken,
) {
	return requestJob(
		"GeneratePlotGPSTrialCoordsExcel",
		{ clientId, clientName, trialId, currentDateTimeString, quantifiedRegion, includeSubplots, fileName },
		"GET",
		accessToken,
	);
}

export function generateStackedProtocolHeatmapExcel(
	trials,
	clientId,
	clientName,
	isForAdmin,
	currentDateTimeString,
	removeExcluded,
	quantifiedRegion,
	includeSubplots,
	isForCSV,
	isZipped,
	fileName,
	accessToken,
) {
	return requestJob(
		"GenerateStackedProtocolHeatmapExcel",
		{
			clientId,
			clientName,
			isForAdmin,
			currentDateTimeString,
			removeExcluded,
			quantifiedRegion,
			includeSubplots,
			isForCSV,
			isZipped,
			fileName,
		},
		"POST",
		accessToken,
		trials,
	);
}

export function generateTabbedProtocolHeatmapExcel(
	trials,
	clientId,
	clientName,
	isForAdmin,
	currentDateTimeString,
	removeExcluded,
	quantifiedRegion,
	includeSubplots,
	isForCSV,
	fileName,
	accessToken,
) {
	return requestJob(
		"GenerateTabbedProtocolHeatmapExcel",
		{
			clientId,
			clientName,
			isForAdmin,
			currentDateTimeString,
			removeExcluded,
			quantifiedRegion,
			includeSubplots,
			isForCSV,
			fileName,
		},
		"POST",
		accessToken,
		trials,
	);
}

export function generatePlotGPSProtocolCoordsExcel(
	trials,
	clientId,
	clientName,
	currentDateTimeString,
	quantifiedRegion,
	includeSubplots,
	fileName,
	accessToken,
) {
	return requestJob(
		"GeneratePlotGPSProtocolCoordsExcel",
		{ clientId, clientName, currentDateTimeString, quantifiedRegion, includeSubplots, fileName },
		"POST",
		accessToken,
		trials,
	);
}

export function generateStackedFieldHeatmapExcel(
	clientId,
	clientName,
	fieldId,
	currentDateTimeString,
	removeExcluded,
	quantifiedRegion,
	includeSubplots,
	isForCSV,
	fileName,
	accessToken,
) {
	return requestJob(
		"GenerateStackedFieldHeatmapExcel",
		{
			clientId,
			clientName,
			fieldId,
			currentDateTimeString,
			removeExcluded,
			quantifiedRegion,
			includeSubplots,
			isForCSV,
			fileName,
		},
		"GET",
		accessToken,
	);
}

export function generateTabbedFieldHeatmapExcel(
	clientId,
	clientName,
	fieldId,
	currentDateTimeString,
	removeExcluded,
	quantifiedRegion,
	includeSubplots,
	isForCSV,
	fileName,
	accessToken,
) {
	return requestJob(
		"GenerateTabbedFieldHeatmapExcel",
		{
			clientId,
			clientName,
			fieldId,
			currentDateTimeString,
			removeExcluded,
			quantifiedRegion,
			includeSubplots,
			isForCSV,
			fileName,
		},
		"GET",
		accessToken,
	);
}

export function generatePlotGPSFieldCoordsExcel(
	clientId,
	clientName,
	fieldId,
	currentDateTimeString,
	quantifiedRegion,
	includeSubplots,
	fileName,
	accessToken,
) {
	return requestJob(
		"GeneratePlotGPSFieldCoordsExcel",
		{ clientId, clientName, fieldId, currentDateTimeString, quantifiedRegion, includeSubplots, fileName },
		"GET",
		accessToken,
	);
}

/**
 * Poll a queued export. `waitSeconds` is a server-side wait, not a timeout — the call returns as
 * soon as the job finishes, or with the current non-terminal status once the wait elapses.
 * `downloadUrl` is minted fresh on every call, so an expired link just needs another call here.
 */
export function getExcelExportStatus(clientId, jobId, waitSeconds, accessToken) {
	return requestJob("GetExcelExportStatus", { clientId, jobId, waitSeconds }, "GET", accessToken);
}
